import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { en, type MessageKey } from './en';
import { tr } from './tr';

export type Locale = 'en' | 'tr';

export const LOCALES: { value: Locale; label: string; flag: string }[] = [
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'tr', label: 'Türkçe', flag: '🇹🇷' },
];

const CATALOGS: Record<Locale, Record<string, string>> = { en, tr };

/**
 * Where the choice is remembered. A panel that forgets the language on every
 * reload is a panel nobody switches, so this is not optional polish.
 */
const STORAGE_KEY = 'prepzio-admin-locale';

function isLocale(value: unknown): value is Locale {
  return value === 'en' || value === 'tr';
}

/**
 * Stored choice first, then the browser's preference, then English.
 *
 * The browser tag is matched on its primary subtag only (`tr-TR` → `tr`), and
 * anything we do not ship falls to English rather than to a half-empty catalog.
 */
function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Private mode / blocked storage: fall through to the browser preference.
  }
  for (const tag of navigator.languages ?? [navigator.language]) {
    const primary = tag?.toLowerCase().split('-')[0];
    if (isLocale(primary)) return primary;
  }
  return 'en';
}

export type TranslateFn = (key: MessageKey, params?: Record<string, string | number>) => string;

type I18nValue = {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: TranslateFn;
};

/**
 * Exported because `ErrorBoundary` is a class component — a boundary cannot be
 * a function component, so it cannot call `useT()` and reads the context
 * directly via `static contextType`.
 */
export const I18nContext = createContext<I18nValue | null>(null);

/** Substitutes `{name}` placeholders. Unknown placeholders are left as-is. */
function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  // Keep `<html lang>` in step: screen readers and the browser's own
  // translation prompt both read it, and a Turkish panel announcing itself as
  // English is read out with English phonetics.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage is a convenience here; the session still switches without it.
    }
  }, []);

  const t = useCallback<TranslateFn>(
    (key, params) => {
      // `en` is the fallback rather than the key itself: an untranslated string
      // should read as English, not as `generate.submit`.
      const message = CATALOGS[locale][key] ?? en[key] ?? key;
      return interpolate(message, params);
    },
    [locale],
  );

  const value = useMemo<I18nValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/** Convenience for the common case — a component that only needs `t`. */
export function useT(): TranslateFn {
  return useI18n().t;
}

/**
 * Picks between a singular and a plural message by count, passing `count` in.
 *
 * Two explicit keys rather than a `_one` suffix derived from one key: the
 * suffixed form cannot be type-checked, so a missing plural would fail at
 * render instead of at build. Turkish does not inflect a noun after a numeral
 * ("5 soru", not "5 sorular"), so its two forms are usually identical — that is
 * fine and deliberate; forcing the catalogs to share a shape English needs and
 * Turkish does not is how a translation ends up ungrammatical in one of them.
 */
export function usePlural(): (
  one: MessageKey,
  many: MessageKey,
  count: number,
  params?: Record<string, string | number>,
) => string {
  const { t } = useI18n();
  return useCallback(
    (one, many, count, params) => t(count === 1 ? one : many, { count, ...params }),
    [t],
  );
}

/**
 * Labels an API enum value (`published`, `free_text`, `hard`).
 *
 * These arrive from the server as raw identifiers and are rendered straight
 * into badges all over the panel. A value with no catalog entry falls back to
 * itself rather than printing `enum.status.archived` at the operator — a new
 * server-side state should look untranslated, not broken.
 */
export function useEnumLabel(): (kind: 'status' | 'type' | 'difficulty', value: string) => string {
  const { t } = useI18n();
  return useCallback(
    (kind, value) => {
      const key = `enum.${kind}.${value}` as MessageKey;
      return key in en ? t(key) : value;
    },
    [t],
  );
}

/**
 * Labels a rejection reason (`factually_wrong`, `judge_blocked`).
 *
 * Shared by the review queue, which offers the reasons, and question flow,
 * which counts them — the two screens must not describe the same bank in
 * different words. The closed list lives in the API, so a reason the catalog
 * does not know prints itself.
 */
export function useReasonLabel(): (reason: string) => string {
  const { t } = useI18n();
  return useCallback(
    (reason) => {
      const key = `reason.${reason}` as MessageKey;
      return key in en ? t(key) : reason;
    },
    [t],
  );
}

export type { MessageKey };
